import React from 'react';
import { Link } from 'react-router-dom';
import { nativeAppLaunchUrl } from '../../lib/nativeRecoveryContinuation';
import { isAdminEmail } from '../../lib/adminAllowlist';
import { Platform } from '../../lib/platform';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '../../config/appStore';
import NativeRecoveryAction from './NativeRecoveryAction';
import UtilityPageFrame from './UtilityPageFrame';

interface RecoveryStoreLinksProps {
  platform: Platform;
  onAppStoreClick: (e: React.MouseEvent) => void;
  onPlayStoreClick: (e: React.MouseEvent) => void;
}

export const RecoveryStoreLinks: React.FC<RecoveryStoreLinksProps> = ({ platform, onAppStoreClick, onPlayStoreClick }) => (
  <>
    {(platform === 'ios' || platform === 'other') && (
      <a href={APP_STORE_URL} className="utility-button utility-button--quiet" style={{ textAlign: 'center', textDecoration: 'none', marginBottom: '0.75rem' }} onClick={onAppStoreClick}>
        Get RidePulse on the App Store
      </a>
    )}
    {(platform === 'android' || platform === 'other') && (
      <a href={GOOGLE_PLAY_URL} className="utility-button utility-button--quiet" style={{ textAlign: 'center', textDecoration: 'none', marginBottom: '0.75rem' }} onClick={onPlayStoreClick}>
        Get RidePulse on Google Play
      </a>
    )}
  </>
);

interface RecoveryPasswordFormProps {
  accountEmail: string;
  newPassword: string;
  confirmPassword: string;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  updating: boolean;
  message: { text: string; type: 'success' | 'error' } | null;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  minPasswordLength: number;
}

export const RecoveryPasswordForm: React.FC<RecoveryPasswordFormProps> = ({
  accountEmail,
  newPassword,
  confirmPassword,
  onNewPasswordChange,
  onConfirmPasswordChange,
  updating,
  message,
  onSubmit,
  onCancel,
  minPasswordLength,
}) => (
  <UtilityPageFrame title="Set a new password" context={
    <p className="utility-muted">
      Choose a new password for <strong style={{ color: 'var(--rp-text)' }}>{accountEmail}</strong>.
      You'll finish right here in this browser.
    </p>
  }>
    <div className="utility-card">
      <form onSubmit={onSubmit}>
        <label className="utility-field">
          <span className="utility-label">New password</span>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={e => onNewPasswordChange(e.target.value)}
            className="utility-input"
            disabled={updating}
            minLength={minPasswordLength}
            required
          />
        </label>
        <label className="utility-field">
          <span className="utility-label">Confirm password</span>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={e => onConfirmPasswordChange(e.target.value)}
            className="utility-input"
            disabled={updating}
            minLength={minPasswordLength}
            required
          />
        </label>

        <ul className="utility-muted--small" style={{ listStyle: 'disc', paddingLeft: '1.25rem', margin: '0 0 1rem' }}>
          <li>At least {minPasswordLength} characters</li>
          <li>One uppercase letter</li>
          <li>One lowercase letter</li>
          <li>One number</li>
        </ul>

        <div aria-live="polite">
          {message && (
            <div className={`utility-message ${message.type === 'error' ? 'utility-message--error' : 'utility-message--success'}`} role={message.type === 'error' ? 'alert' : 'status'}>
              {message.text}
            </div>
          )}
        </div>

        <button type="submit" className="utility-button" disabled={updating || !newPassword || !confirmPassword}>
          {updating ? 'Updating...' : 'Update password'}
        </button>
        <button type="button" onClick={onCancel} className="utility-button utility-button--quiet" style={{ marginTop: '0.5rem' }}>
          Cancel
        </button>
      </form>
    </div>
  </UtilityPageFrame>
);

export const RecoveryVerifying: React.FC = () => (
  <UtilityPageFrame title="Password reset">
    <div className="utility-card" style={{ textAlign: 'center' }}>
      <div className="utility-spinner" role="status" aria-label="Verifying reset link" />
      <p className="utility-muted" aria-live="polite">Verifying your reset link...</p>
    </div>
  </UtilityPageFrame>
);

interface RecoverySuccessProps {
  platform: Platform;
  accountEmail: string;
}

export const RecoverySuccess: React.FC<RecoverySuccessProps> = ({ platform, accountEmail }) => (
  <UtilityPageFrame title="Password updated" context={
    <p className="utility-muted">
      Your new password is set. You can now sign in to RidePulse with it —
      no further step is needed here.
    </p>
  }>
    <div className="utility-card">
      <div className="utility-message utility-message--success" role="status">
        Password updated successfully.
      </div>
      {platform !== 'other' && (
        <NativeRecoveryAction
          platform={platform}
          url={nativeAppLaunchUrl(platform)}
          note="Open the app and sign in with your new password."
          fallbackNote="RidePulse did not open. If it's installed, open it directly and sign in — your new password is already set."
        />
      )}
      {isAdminEmail(accountEmail) && (
        <Link to="/admin" className="utility-button utility-button--quiet" style={{ textAlign: 'center', textDecoration: 'none', marginBottom: '0.75rem', display: 'block' }}>
          Sign in to the admin panel
        </Link>
      )}
      <p className="utility-muted utility-muted--small" style={{ marginTop: '1rem', textAlign: 'center' }}>
        Finished? You can close this page.
      </p>
    </div>
  </UtilityPageFrame>
);
